import { Gallery } from 'iconsax-react';
import { isNil, take, without } from 'lodash-es';
import { type FormEvent, useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import GroupDefaultAvatarIcon from '@/assets/images/contact/group-default-avatar.svg?react';
import { UserCarousel, type UserCarouselItem } from '@/components/common/user-carousel/user-carousel';
import { UploadImageField } from '@/components/fields/upload-image-field';
import useFriends from '@/hooks/contacts/use-friends';
import useSystemSetting from '@/hooks/files/use-system-setting';
import type { CreateGroupFormData } from '@/hooks/groups/use-create-group';
import useUserInfo from '@/hooks/users/use-user-info';
import { useAuthStore } from '@/stores/auth';
import cn from '@/utils/merge-class-names';
import FormInput from '../common/form-input';
import UserAvatar from '../common/user-carousel/user-avatar';
import SelectContactList from './contact-list/select-contact-list';

type Props = {
  step: number;
  onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
};

export default function CreateGroupForm({ step, onSubmit }: Props) {
  const { control, watch, resetField, setValue, getFieldState } = useFormContext<CreateGroupFormData>();
  const { field } = useController({ control, name: 'UserIDs' });
  const selfUserId = useAuthStore((s) => s.auth?.UserID);
  const { t } = useTranslation();
  const { friends } = useFriends();
  const { data: userInfo } = useUserInfo();
  const { getFileSizeLimit } = useSystemSetting();
  const memberIds = watch('UserIDs', []);

  const headImageSizeLimit = useMemo(() => getFileSizeLimit('image'), [getFileSizeLimit]);

  const selectedFriends = useMemo(() => {
    const selected = memberIds
      .map((userId) => {
        const member = friends[userId];

        return member
          ? ({
              userId: member.FriendID,
              avatarUrl: member.FriendHeadImg,
              label: member.RemarkName || member.FriendNickName,
            } satisfies UserCarouselItem)
          : undefined;
      })
      .filter((v) => !isNil(v));

    if (userInfo) {
      selected.unshift({
        userId: userInfo.UID,
        avatarUrl: userInfo.HeadImg,
        label: userInfo.NickName,
      });
    }

    return selected;
  }, [memberIds, friends, userInfo]);

  useEffect(() => {
    if (step === 0) {
      resetField('GroupName', { defaultValue: '' });
    } else if (!getFieldState('GroupName').isDirty) {
      const groupName = generateDefaultGroupNameByMembers(selectedFriends);
      setValue('GroupName', groupName);
    }
  }, [selectedFriends, step, getFieldState, resetField, setValue]);

  return (
    <form
      className='flex w-full flex-1 flex-col'
      onSubmit={onSubmit}>
      <div className={cn('flex w-full px-5 gap-y-6 mt-4 pb-2 flex-col items-center', step ? 'border-b-4' : 'hidden')}>
        <div className='relative mr-6 size-20'>
          <UploadImageField
            name='GroupImg'
            control={control}
            className='size-20'
            limitSize={headImageSizeLimit}
            fallback={
              <div className='relative size-20'>
                <div className='bg-primary rounded-full flex flex-center size-20 p-5'>
                  <GroupDefaultAvatarIcon className='size-10' />
                </div>

                <div className='btn btn-circle btn-xs absolute bottom-0 right-0 bg-neutral-0 shadow-xl'>
                  <Gallery className='size-3 text-secondary' />
                </div>
              </div>
            }>
            {(fieldValue, preview) => (
              <div className='inline-flex'>
                <img
                  src={preview || fieldValue?.url || ''}
                  alt='group avatar'
                  className='rounded-full aspect-square object-center object-cover'
                  width={80}
                  height={80}
                  fetchPriority='low'
                />

                <div className='btn btn-circle btn-xs absolute bottom-0 right-0 bg-neutral-0 shadow-xl'>
                  <Gallery className='size-3 text-secondary' />
                </div>
              </div>
            )}
          </UploadImageField>
        </div>

        <label
          htmlFor='GroupName'
          className='flex flex-1 w-full'>
          <FormInput
            control={control}
            name='GroupName'
            className='w-full'
            defaultValue=''
            maxLength={50}
            helperText={t('fields.messages.max-length', { name: t('contact.create-group.group-name'), count: 50 })}
          />
        </label>
      </div>
      {step === 0 ? (
        <SelectContactList
          canSelectAll={false}
          selectedIds={field.value}
          onSelect={(ids) => {
            field.onChange(ids);
          }}
          disableRemove={!!step && field.value.length === 1}
          showOnlineDesc={false}
        />
      ) : (
        <div className='flex flex-col flex-1 mx-5 mt-4 mb-18'>
          <span className='mb-4 text-sm leading-6 font-bold'>{`${t('conversation.setting.group.member')} (${selectedFriends.length})`}</span>

          <div className='grid auto-rows-fr grid-cols-4 gap-x-6 gap-y-4'>
            {selectedFriends.map((user) => (
              <UserAvatar
                className='w-16 h-22 overflow-hidden px-1'
                size='size-14'
                key={user.userId}
                {...user}
                onRemove={
                  user.userId === selfUserId ? undefined : () => field.onChange(without(field.value, user.userId))
                }
              />
            ))}
          </div>
        </div>
      )}
    </form>
  );
}

function generateDefaultGroupNameByMembers(members: UserCarouselItem[]): string {
  const names = take(members, 2).map((member) => member.label);
  return `${names.join('、')}...`;
}
